import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { addKlachtAction, refundWithCredit, KlachtError } from '../../../lib/klachten';
import { REFUND_METHODES } from '../../../lib/klacht-constants';
import { formatCurrency } from '../../../lib/format';
import { useKlachtDialogStore } from '../../../stores/klacht-dialog-store';

interface Props {
  onAdded?: () => void;
}

/**
 * Action-dialog: refund. Methode bepaalt de uitvoer-route in de Apex
 * RefundActionHandler:
 *  - Mollie         → calls MollieRefundService.refund() met webhookUrl
 *  - Bank           → handmatige overschrijving, status start Pending
 *  - Coulance_Tegoed → genereert tegoed-record i.p.v. cash refund
 *  - Factuur_Correctie → corrigeert open factuur
 *
 * Voor Mollie-methode is `molliePaymentId` op de werkorder vereist; we
 * geven die door als `params.molliePaymentId` zodat Apex hem kan vinden.
 */
export function AddRefundDialog({ onAdded }: Props) {
  const openDialog = useKlachtDialogStore((s) => s.openDialog);
  const payload = useKlachtDialogStore((s) => s.payload);
  const close = useKlachtDialogStore((s) => s.close);

  const isOpen = openDialog === 'addRefund';
  const klachtId = payload.klachtId;
  const werkOrderId = payload.werkOrderId;
  const molliePaymentId = (payload.extra?.molliePaymentId as string | undefined) ?? null;

  const [bedrag, setBedrag] = useState('');
  const [methode, setMethode] = useState<string>(REFUND_METHODES[0].code);
  const [reden, setReden] = useState('');
  const [maakCreditfactuur, setMaakCreditfactuur] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Partial-failure-herstel: creditfactuur is aangemaakt maar de terugstorting
  // mislukte. Re-submit is veilig: de creditstap is idempotent (zelfde
  // creditfactuur terug), dus een retry stort alleen alsnog terug.
  const [partial, setPartial] = useState<{
    creditNaam: string | null;
    message: string;
    retryable: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setBedrag('');
      setMethode(molliePaymentId ? 'Mollie' : 'Bank');
      setReden('');
      setMaakCreditfactuur(true);
      setSubmitting(false);
      setPartial(null);
      setError(null);
    }
  }, [isOpen, molliePaymentId]);

  const bedragNum = Number(bedrag.replace(',', '.'));
  const bedragValid = Number.isFinite(bedragNum) && bedragNum > 0;
  const mollieUnavailable = methode === 'Mollie' && !molliePaymentId;
  // Een creditfactuur kan alleen bij een cash-refund (Mollie/Bank) op een
  // gefactureerde order. Voor Coulance_Tegoed/Factuur_Correctie niet van toepassing.
  const creditEligible = methode === 'Mollie' || methode === 'Bank';
  const withCredit = maakCreditfactuur && creditEligible;

  const handleSubmit = useCallback(async () => {
    if (!klachtId) {
      setError('Geen klacht-id meegegeven');
      return;
    }
    if (!bedragValid) {
      setError('Bedrag moet groter zijn dan €0');
      return;
    }
    if (!reden.trim()) {
      setError('Reden is verplicht');
      return;
    }
    if (mollieUnavailable) {
      setError(
        'Originele Mollie-payment-Id ontbreekt op deze werkorder; kies methode Bank.'
      );
      return;
    }
    if (withCredit && !werkOrderId) {
      setError('Geen werkorder-id beschikbaar; creditfactuur niet mogelijk. Zet het vinkje uit.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (withCredit) {
        const r = await refundWithCredit({
          klachtId,
          werkOrderId: werkOrderId!,
          bedragInclBtw: bedragNum,
          methode: methode as 'Mollie' | 'Bank',
          reden: reden.trim(),
          molliePaymentId: methode === 'Mollie' ? molliePaymentId : null,
        });
        if (r.stepErrors && r.stepErrors.length > 0) {
          // Creditfactuur is wél aangemaakt, de terugstorting niet. Opnieuw
          // indienen is veilig (idempotente creditstap), behalve als er al een
          // Refund__c op Mislukt staat: dan geen retry-lus maar handmatig.
          const misluktEdge = r.stepErrors.some(
            (e) =>
              ((e.code || '').includes('IDEMPOTENT_DUPLICATE') ||
                (e.message || '').includes('IDEMPOTENT_DUPLICATE')) &&
              (e.message || '').includes('Mislukt')
          );
          setPartial({
            creditNaam: r.creditInvoiceNaam ?? null,
            message: r.stepErrors.map((e) => e.message || e.code || 'onbekende fout').join('; '),
            retryable: !misluktEdge,
          });
          setError(null);
          onAdded?.();
          return;
        }
        close();
        onAdded?.();
        return;
      }
      await addKlachtAction({
        klachtId,
        actionType: 'Refund',
        params: {
          bedragInclBtw: bedragNum,
          methode,
          reden: reden.trim(),
          werkOrderId: werkOrderId ?? null,
          molliePaymentId: methode === 'Mollie' ? molliePaymentId : null,
        },
      });
      close();
      onAdded?.();
    } catch (err) {
      setError(err instanceof KlachtError ? err.message : 'Refund toevoegen mislukt');
    } finally {
      setSubmitting(false);
    }
  }, [klachtId, bedragNum, bedragValid, methode, reden, mollieUnavailable, withCredit, werkOrderId, molliePaymentId, close, onAdded]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Refund toevoegen</h2>
          <p className="text-xs text-slate-500 mt-1">
            Volg de juiste methode op basis van hoe de klant origineel betaald heeft.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <label className="block">
            <span className="text-sm text-slate-700">
              Bedrag (incl. BTW) <span className="text-red-600">*</span>
            </span>
            <div className="mt-1 flex items-center border border-slate-300 rounded">
              <span className="px-2 text-sm text-slate-500">€</span>
              <input
                type="text"
                inputMode="decimal"
                value={bedrag}
                onChange={(e) => setBedrag(e.target.value)}
                placeholder="0,00"
                className="flex-1 px-2 py-1.5 text-sm outline-none"
                disabled={submitting || partial !== null}
              />
            </div>
            {bedragValid && (
              <p className="mt-1 text-xs text-slate-500">{formatCurrency(bedragNum)}</p>
            )}
          </label>

          <label className="block">
            <span className="text-sm text-slate-700">
              Methode <span className="text-red-600">*</span>
            </span>
            <select
              value={methode}
              onChange={(e) => setMethode(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
              disabled={submitting || partial !== null}
            >
              {REFUND_METHODES.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.label}
                </option>
              ))}
            </select>
            {mollieUnavailable && (
              <p className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Geen Mollie-payment-Id op deze werkorder. Kies een andere methode.
              </p>
            )}
          </label>

          <label className="block">
            <span className="text-sm text-slate-700">
              Reden <span className="text-red-600">*</span>
            </span>
            <input
              type="text"
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              placeholder="Bv. compensatie voor te late levering"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting || partial !== null}
            />
          </label>

          {creditEligible && (
            <label className="flex items-start gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2">
              <input
                type="checkbox"
                checked={maakCreditfactuur}
                onChange={(e) => setMaakCreditfactuur(e.target.checked)}
                disabled={submitting || partial !== null}
                className="mt-0.5"
              />
              <span className="text-xs text-slate-600">
                <span className="font-medium text-slate-800">Ook creditfactuur aanmaken</span> —
                maakt een creditnota op de originele factuur en verrekent/boekt de terugstorting,
                zodat het administratief klopt. De creditnota wordt niet automatisch gemaild
                (doe dat zelf vanaf de factuur).
              </span>
            </label>
          )}

          {partial && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 space-y-1">
              <p>
                <span className="font-medium">
                  Creditfactuur {partial.creditNaam ?? ''} is aangemaakt.
                </span>{' '}
                De terugstorting mislukte: {partial.message}
              </p>
              <p>
                {partial.retryable
                  ? 'Opnieuw proberen is veilig: er wordt geen tweede creditfactuur gemaakt.'
                  : 'De terugstorting staat op Mislukt. Handel deze handmatig af via de factuur.'}
              </p>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            {partial ? 'Sluiten (terugstorting later)' : 'Sluiten'}
          </button>
          {(!partial || partial.retryable) && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !bedragValid || !reden.trim() || mollieUnavailable}
              className={clsx(
                'text-sm font-medium px-3 py-1.5 rounded',
                submitting || !bedragValid || !reden.trim() || mollieUnavailable
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              )}
            >
              {submitting
                ? 'Bezig…'
                : partial
                  ? 'Terugstorting opnieuw proberen'
                  : withCredit
                    ? 'Refund + creditfactuur'
                    : 'Refund aanmaken'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
